import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { api, setUser, setToken } from "../lib/api";
import { Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * AuthCallback — landing page for the Emergent-managed Google OAuth flow.
 *
 * Emergent redirects the browser here with `#session_id=…` in the URL hash.
 * Two branches, one page:
 *
 *   1. Native user (Capacitor iOS/Android): the OAuth happened inside Safari
 *      (not the Capacitor WebView), so the app's storage jar is empty and
 *      completing the exchange in Safari doesn't help the app. We hand the
 *      session_id back to the native app via the `binshare://` custom URL
 *      scheme so the app can complete the exchange itself.
 *
 *   2. Web user: exchange the session_id via `/auth/google-callback`, stash
 *      the token, forward to /dashboard exactly like before.
 *
 * We can't reliably detect "native user in Safari" from Safari itself
 * (Capacitor's user-agent is only set inside the WebView). So we ALWAYS
 * attempt the `binshare://` handoff, and simultaneously kick off the web
 * exchange as a fallback so the page still works for pure web users.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const [phase, setPhase] = useState("exchanging"); // exchanging | need_return_to_app | error
  const sessionId = (() => {
    if (typeof window === "undefined") return null;
    const h = window.location.hash;
    if (!h.includes("session_id=")) return null;
    return new URLSearchParams(h.slice(1)).get("session_id");
  })();

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!sessionId) {
      setPhase("error");
      toast.error("No Google sign-in session was returned. Please try again.");
      navigate("/login", { replace: true });
      return;
    }

    // 1) Fire the native app deep-link IMMEDIATELY. iOS will prompt "Open in
    //    Binshare?" if the app is installed; if not installed or user
    //    dismisses, the page stays here and the web-exchange below completes.
    try {
      window.location.href = `binshare://auth?session_id=${encodeURIComponent(sessionId)}`;
    } catch {
      // Some browsers throw on unknown schemes — safe to swallow.
    }

    // 2) In parallel, complete the exchange for web users. If iOS handed off
    //    to the app, this still runs (harmlessly) in the backgrounded Safari
    //    tab and just times out — the tokens end up in Safari's storage,
    //    which the native app doesn't care about.
    (async () => {
      try {
        const { data } = await api.post("/auth/google-callback", { session_id: sessionId });
        setToken(data.token);
        setUser(data.user);
        window.history.replaceState({}, "", "/auth/callback"); // strip hash
        toast.success(`Welcome${data.user?.name ? `, ${data.user.name}` : ""}!`);
        if (data.user?.needs_role_selection) {
          navigate("/onboarding/role", { replace: true });
          return;
        }
        // Native users won't see this — Safari would have already handed off
        // to the app via the URL scheme. Web users land on the dashboard.
        setPhase("need_return_to_app");
        // Auto-forward to dashboard after a short beat so pure-web users
        // don't get stuck on this bridge page.
        setTimeout(() => {
          if (ranRef.current) navigate("/dashboard", { replace: true });
        }, 1500);
      } catch (err) {
        setPhase("error");
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        setTimeout(() => navigate("/login", { replace: true }), 1200);
      }
    })();
  }, [sessionId, navigate]);

  const retryReturnToApp = () => {
    if (!sessionId) return;
    window.location.href = `binshare://auth?session_id=${encodeURIComponent(sessionId)}`;
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-5 py-20 text-center">
        <Loader2 className="w-16 h-16 text-[#4A6B56] mx-auto animate-spin" />
        <h1 className="mt-4 text-3xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
          Signing you in…
        </h1>
        <p className="mt-2 text-[#6B6965]">
          If you started sign-in from the Binshare mobile app, tap the button
          below to return there. Otherwise you&apos;ll be taken to the web
          dashboard automatically.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button
            data-testid="auth-return-to-app-btn"
            onClick={retryReturnToApp}
            className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Return to Binshare app
          </Button>
          {phase === "need_return_to_app" && (
            <Button
              data-testid="auth-continue-web-btn"
              variant="outline"
              onClick={() => navigate("/dashboard", { replace: true })}
              className="rounded-full border-[#1B3624] text-[#1B3624] hover:bg-[#1B3624]/5"
            >
              Continue on web
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
